document.addEventListener("DOMContentLoaded", () => {
  const track = document.getElementById("carouselTrack");
  const prevBtn = document.getElementById("prevBtn");
  const nextBtn = document.getElementById("nextBtn");
  const indicators = document.getElementById("indicators");

  if (!track) return;

  // Detect if we're using metafield galleries
  const useMetafield = track.hasAttribute('data-use-metafield');

  // Configuration
  let config = {};
  try {
    const configScript = document.getElementById("desktop-gallery-config");
    if (configScript) {
      config = JSON.parse(configScript.textContent);
    }
  } catch (e) {
    console.error("Failed to parse gallery config:", e);
  }

  const IMAGE_SIZE = config.imageSize || 800;
  const SHOP_URL = config.shopUrl || window.location.origin;
  const PRODUCT_HANDLE = config.productHandle;

  // Gallery cache - keyed by COLOR (not variant ID)
  let galleriesCache = {};
  if (useMetafield) {
    try {
      const galleryScript = document.getElementById("variant-galleries-data");
      if (galleryScript) {
        const variantGalleries = JSON.parse(galleryScript.textContent);
        // Store by current color
        if (config.currentColor && variantGalleries[config.currentVariantId]) {
          galleriesCache[config.currentColor] = variantGalleries[config.currentVariantId];
        }
        console.log("Initial gallery loaded for color:", config.currentColor);
      }
    } catch (e) {
      console.error("Failed to parse variant galleries:", e);
    }
  }

  // Map variant IDs to their color
  let variantColorMap = {};
  try {
    const colorMapScript = document.getElementById("desktop-variant-color-map");
    if (colorMapScript) {
      variantColorMap = JSON.parse(colorMapScript.textContent);
    }
  } catch (e) {
    console.error("Failed to parse variant color map:", e);
  }

  // First images for each color
  let colorFirstImages = {};
  try {
    const firstImagesScript = document.getElementById("desktop-color-first-images");
    if (firstImagesScript) {
      colorFirstImages = JSON.parse(firstImagesScript.textContent);
    }
  } catch (e) {
    console.error("Failed to parse color first images:", e);
  }

  let currentVariantId = config.currentVariantId;
  let currentColor = config.currentColor;

  // State
  let currentIndex = 0;
  let isDragging = false;
  let startX = 0, startY = 0, deltaX = 0, deltaY = 0;
  let isHorizontalSwipe = null, startTime = 0;

  // Track if a gallery switch is in progress to prevent race conditions
  let switchAbortController = null;

  // Tuning constants
  const MOVE_THRESHOLD = 20;
  const DIRECTION_RATIO = 1.6;
  const SWIPE_DISTANCE_RATIO = 0.15;
  const SWIPE_VELOCITY = 0.35;
  const MIN_SWIPE_DISTANCE = 50;

  // Maps for fallback mode
  const variantIdToSlide = new Map();
  const optionKeyToSlide = new Map();

  /**
   * Fix and normalize Shopify image URL
   */
  function fixShopifyUrl(url) {
    if (!url) return url;
    
    let fixedUrl = url;
    
    if (fixedUrl.includes('/products/files/')) {
      fixedUrl = fixedUrl.replace('/products/files/', '/cdn/shop/files/');
    }
    
    if (fixedUrl.startsWith('//')) {
      fixedUrl = 'https:' + fixedUrl;
    } else if (!fixedUrl.startsWith('http')) {
      if (fixedUrl.startsWith('/cdn/shop/')) {
        fixedUrl = SHOP_URL + fixedUrl;
      } else if (fixedUrl.startsWith('/files/')) {
        fixedUrl = SHOP_URL + '/cdn/shop' + fixedUrl;
      } else if (fixedUrl.startsWith('/')) {
        fixedUrl = SHOP_URL + fixedUrl;
      } else {
        fixedUrl = SHOP_URL + '/cdn/shop/files/' + fixedUrl;
      }
    }
    
    return fixedUrl;
  }

  /**
   * Resize Shopify CDN image URL
   */
  function resizeImageUrl(url, size) {
    if (!url) return url;
    
    let fixedUrl = fixShopifyUrl(url);
    
    if (fixedUrl.includes('cdn.shopify.com') || fixedUrl.includes('/cdn/shop/')) {
      const separator = fixedUrl.includes('?') ? '&' : '?';
      return fixedUrl + separator + 'width=' + size + '&quality=80';
    }
    
    return fixedUrl;
  }

  /**
   * Get full size URL (for zoom)
   */
  function getFullSizeUrl(url) {
    if (!url) return url;
    
    let fixedUrl = fixShopifyUrl(url);
    
    if (fixedUrl.includes('cdn.shopify.com') || fixedUrl.includes('/cdn/shop/')) {
      return fixedUrl
        .replace(/[&?]width=\d+/g, '')
        .replace(/[&?]height=\d+/g, '')
        .replace(/[&?]quality=\d+/g, '')
        .replace(/\?&/, '?')
        .replace(/\?$/, '');
    }
    
    return fixedUrl;
  }

  /**
   * Preload an array of gallery items (images/videos).
   * Returns a Promise that resolves when all images are loaded (or after timeout).
   * Videos resolve immediately since they stream.
   */
  function preloadGallery(gallery) {
    const promises = gallery.map((item) => {
      const media = detectMediaType(item);
      if (!media) return Promise.resolve();
      
      if (media.type === 'video') return Promise.resolve();
      
      const url = resizeImageUrl(fixShopifyUrl(media.src), IMAGE_SIZE);
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = url;
        setTimeout(resolve, 5000);
      });
    });
    
    return Promise.all(promises);
  }

  /**
   * Preload just the first image of a gallery for quick swap.
   */
  function preloadFirstImage(gallery) {
    if (!gallery || gallery.length === 0) return Promise.resolve();
    const media = detectMediaType(gallery[0]);
    if (!media || media.type === 'video') return Promise.resolve();
    
    const url = resizeImageUrl(fixShopifyUrl(media.src), IMAGE_SIZE);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = url;
      setTimeout(resolve, 3000);
    });
  }

  /**
   * Show a subtle loading overlay on the carousel (doesn't remove current images)
   */
  function showLoadingOverlay() {
    let overlay = document.getElementById('desktop-gallery-loading-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'desktop-gallery-loading-overlay';
      overlay.style.cssText = 'position:absolute;top:0;left:0;right:0;bottom:0;z-index:5;display:flex;align-items:flex-start;justify-content:center;padding-top:16px;pointer-events:none;';
      overlay.innerHTML = '<div style="background:rgba(0,0,0,0.5);color:#fff;padding:5px 16px;border-radius:20px;font-size:13px;backdrop-filter:blur(4px);">Loading...</div>';
      const wrapper = track.parentElement;
      if (wrapper) {
        wrapper.style.position = 'relative';
        wrapper.appendChild(overlay);
      }
    }
    overlay.style.display = 'flex';
  }

  /**
   * Hide the loading overlay
   */
  function hideLoadingOverlay() {
    const overlay = document.getElementById('desktop-gallery-loading-overlay');
    if (overlay) overlay.style.display = 'none';
  }

  // Build maps from DOM (only for fallback mode)
  function buildMaps() {
    if (useMetafield) return;

    const currentSlides = Array.from(document.querySelectorAll(".carousel-slide"));
    variantIdToSlide.clear();
    optionKeyToSlide.clear();

    for (let i = 0; i < currentSlides.length; i++) {
      const containers = Array.from(currentSlides[i].querySelectorAll(".image-container"));
      for (const c of containers) {
        const idsRaw = (c.dataset.variantIds || "");
        const ids = idsRaw.split("|").filter(Boolean);
        ids.forEach(id => {
          const key = String(id);
          if (!variantIdToSlide.has(key)) variantIdToSlide.set(key, i);
        });

        const vsRaw = (c.dataset.variants || "");
        const optionKeys = vsRaw.split("|").filter(Boolean);
        optionKeys.forEach(k => {
          if (!optionKeyToSlide.has(k)) optionKeyToSlide.set(k, i);
        });
      }
    }
  }

  // Improved media item detection
  function detectMediaType(item) {
    if (!item) return null;
    
    if (item.media_type === 'video') {
      let bestSource = null;
      if (item.sources && item.sources.length > 0) {
        const mp4Sources = item.sources.filter(source => source.format === 'mp4');
        if (mp4Sources.length > 0) {
          mp4Sources.sort((a, b) => (b.height || 0) - (a.height || 0));
          bestSource = mp4Sources.find(source => source.height >= 720) || mp4Sources[0];
        } else {
          bestSource = item.sources[0];
        }
      }
      
      return {
        type: 'video',
        src: bestSource ? bestSource.url : (item.sources && item.sources.length > 0 ? item.sources[0].url : null),
        poster: item.preview_image ? item.preview_image.src : null,
        aspect_ratio: item.aspect_ratio || 0.75
      };
    }

    if (item.src) {
      const src = item.src.toLowerCase();
      if (src.includes('.mp4') || src.includes('.webm') || src.includes('.mov')) {
        return { type: 'video', src: item.src, poster: item.preview_image ? item.preview_image.src : null };
      }
      return { type: 'image', src: item.src };
    }
    
    if (typeof item === 'string') {
      const src = item.toLowerCase();
      if (src.includes('.mp4') || src.includes('.webm') || src.includes('.mov')) {
        return { type: 'video', src: item };
      }
      return { type: 'image', src: item };
    }
    
    return null;
  }

  // Helper function to create media element
  function createMediaElement(item, index, isSingle = false) {
    const url = resizeImageUrl(item.src, IMAGE_SIZE);
    const objectFit = isSingle ? 'contain' : 'cover';
    
    if (item.type === 'video') {
      let posterUrl = item.poster ? fixShopifyUrl(item.poster) : '';
      return `
        <video 
          src="${fixShopifyUrl(item.src)}"
          ${posterUrl ? `poster="${posterUrl}"` : ''}
          autoplay muted playsinline loop preload="auto"
          width="100%" height="100%"
          style="object-fit: ${objectFit};"
        ></video>
      `;
    } else {
      return `
        <img 
          src="${url}"
          alt="Variant image ${index}"
          width="100%" height="100%"
          draggable="false"
          loading="${index <= 2 ? 'eager' : 'lazy'}"
        />
      `;
    }
  }

  // Rebuild carousel with new images
  function rebuildCarousel(newGallery) {
    if (!useMetafield) return;
    
    console.log("Rebuilding carousel with:", newGallery?.length, "items");
    
    if (!newGallery || !Array.isArray(newGallery) || newGallery.length === 0) {
      track.innerHTML = '<div class="carousel-slide"><p style="color:red; text-align:center; padding:20px;">No images found for this variant</p></div>';
      indicators.innerHTML = '';
      hideLoadingOverlay();
      return;
    }

    track.innerHTML = '';
    
    // Filter and normalize media items
    const mediaItems = newGallery.map((item, index) => {
      const media = detectMediaType(item);
      if (!media || !media.src) {
        console.warn(`Invalid media item at index ${index}:`, item);
        return null;
      }
      return media;
    }).filter(item => item !== null);

    if (mediaItems.length === 0) {
      track.innerHTML = '<div class="carousel-slide"><p style="color:red; text-align:center; padding:20px;">No valid media found</p></div>';
      indicators.innerHTML = '';
      hideLoadingOverlay();
      return;
    }

    const totalItems = mediaItems.length;
    const fullSlides = Math.floor(totalItems / 2);
    const hasSingleItem = totalItems % 2;
    const slideCount = fullSlides + (hasSingleItem ? 1 : 0);
    
    for (let i = 0; i < slideCount; i++) {
      const slide = document.createElement('div');
      slide.className = 'carousel-slide';
      slide.setAttribute('data-slide-index', i);
      
      const idx1 = i * 2;
      const idx2 = idx1 + 1;
      
      const item1 = mediaItems[idx1] || null;
      const item2 = mediaItems[idx2] || null;
      
      if (i === slideCount - 1 && hasSingleItem === 1) {
        if (item1) {
          const container = document.createElement('div');
          container.className = 'image-container single';
          container.innerHTML = createMediaElement(item1, idx1 + 1, true);
          slide.appendChild(container);
        }
      } else if (item1 && item2) {
        const container1 = document.createElement('div');
        container1.className = 'image-container double';
        container1.innerHTML = createMediaElement(item1, idx1 + 1, false);
        
        const container2 = document.createElement('div');
        container2.className = 'image-container double';
        container2.innerHTML = createMediaElement(item2, idx2 + 1, false);
        
        slide.appendChild(container1);
        slide.appendChild(container2);
      } else if (item1) {
        const container = document.createElement('div');
        container.className = 'image-container single';
        container.innerHTML = createMediaElement(item1, idx1 + 1, true);
        slide.appendChild(container);
      }
      
      track.appendChild(slide);
    }
    
    currentIndex = 0;
    rebuildIndicators();
    updateCarousel();
    hideLoadingOverlay();
    
    // Re-initialize zoom functionality
    setTimeout(() => {
      new CarouselZoom('.custom-carousel-container');
    }, 100);
  }

  // Rebuild thumbnails (one per slide, using slide's first image/video poster)
  function rebuildIndicators() {
    const currentSlides = Array.from(document.querySelectorAll(".carousel-slide"));
    indicators.innerHTML = "";
    currentSlides.forEach((slide, i) => {
      const firstImg = slide.querySelector("img");
      const firstVideo = slide.querySelector("video");
      const isVideo = !!firstVideo && !firstImg;
      let thumbSrc = "";
      if (firstImg) thumbSrc = firstImg.getAttribute("src") || firstImg.dataset.src || "";
      else if (firstVideo) thumbSrc = firstVideo.getAttribute("poster") || "";

      // Resize to small via Shopify CDN params
      if (thumbSrc && (thumbSrc.includes("cdn.shopify.com") || thumbSrc.includes("/cdn/shop/"))) {
        thumbSrc = thumbSrc.replace(/([?&])width=\d+/g, "$1width=160").replace(/([?&])quality=\d+/g, "$1quality=70");
        if (!/[?&]width=/.test(thumbSrc)) {
          thumbSrc += (thumbSrc.includes("?") ? "&" : "?") + "width=160&quality=70";
        }
      }

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `desktop-thumbnail${i === currentIndex ? " active" : ""}`;
      btn.setAttribute("aria-label", `Show image ${i + 1}`);
      btn.innerHTML = `<img src="${thumbSrc}" alt="" loading="lazy" draggable="false">${isVideo ? '<span class="desktop-thumbnail__play" aria-hidden="true">▶</span>' : ""}`;
      btn.addEventListener("click", () => {
        currentIndex = i;
        updateCarousel();
      });
      indicators.appendChild(btn);
    });
  }

  // Update carousel display
  function updateCarousel() {
    const currentSlides = Array.from(document.querySelectorAll(".carousel-slide"));
    if (currentSlides.length === 0) return;
    
    const w = currentSlides[0].offsetWidth || track.offsetWidth || 1;
    track.style.transform = `translateX(-${w * currentIndex}px)`;
    prevBtn.disabled = (currentIndex === 0);
    nextBtn.disabled = (currentIndex === currentSlides.length - 1);

    indicators.querySelectorAll(".desktop-thumbnail").forEach((el, i) => {
      el.classList.toggle("active", i === currentIndex);
    });
    const activeThumb = indicators.querySelector(".desktop-thumbnail.active");
    if (activeThumb) activeThumb.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });

    if (!useMetafield) {
      syncVariantWithSlide();
    }
  }

  // Sync variant with current slide (fallback mode only)
  function syncVariantWithSlide() {
    const currentSlides = Array.from(document.querySelectorAll(".carousel-slide"));
    const slide = currentSlides[currentIndex];
    if (!slide) return;

    const c = slide.querySelector(".image-container[data-variant-ids]");
    if (!c) return;

    const idsRaw = c.dataset.variantIds || "";
    const ids = idsRaw.split("|").filter(Boolean);
    if (ids.length === 0) return;

    const variantId = ids[0];

    const variantSelectEl = document.querySelector("variant-radios, variant-selects, product-interactive-options");
    if (!variantSelectEl) return;

    const variantData = variantSelectEl.getVariantData ? 
      variantSelectEl.getVariantData() : 
      getVariantDataFromDOM();

    const newVariant = variantData.find(v => v.id == variantId);
    if (!newVariant) return;

    const currentSizeInput = document.querySelector('input[name="Size"]:checked');
    const currentSize = currentSizeInput ? currentSizeInput.value : null;

    const colorOption = newVariant.options[0];
    const colorInput = document.querySelector(`input[name="Color"][value="${CSS.escape(colorOption)}"]`);
    if (colorInput && !colorInput.checked) {
      colorInput.checked = true;
      colorInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    const sameColorVariants = variantData.filter(v => v.options[0] === colorOption);
    
    const allSizeInputs = document.querySelectorAll('input[name="Size"]');
    allSizeInputs.forEach(sizeInput => {
      const sizeValue = sizeInput.value;
      const sizeVariant = sameColorVariants.find(v => v.options[1] === sizeValue);
      const isAvailable = sizeVariant && sizeVariant.available;
      
      const label = document.querySelector(`label[for="${sizeInput.id}"]`);
      if (label) {
        if (isAvailable) {
          sizeInput.disabled = false;
          label.classList.remove('unavailable-variant', 'is-disabled');
          label.style.opacity = '';
          label.style.textDecoration = '';
          label.style.color = '';
        } else {
          sizeInput.disabled = false;
          label.classList.add('unavailable-variant', 'is-disabled');
          label.style.opacity = '0.5';
          label.style.textDecoration = 'line-through';
          label.style.color = 'rgb(153, 153, 153)';
        }
      }
    });
    
    const currentSizeAvailable = sameColorVariants.find(v => 
      v.options[1] === currentSize && v.available
    );

    let targetSize = currentSize;

    if (!currentSizeAvailable) {
      const firstAvailableVariant = sameColorVariants.find(v => v.available);
      if (firstAvailableVariant) {
        targetSize = firstAvailableVariant.options[1];
      }
    }

    if (targetSize) {
      const sizeInput = document.querySelector(`input[name="Size"][value="${CSS.escape(targetSize)}"]`);
      if (sizeInput && !sizeInput.checked) {
        sizeInput.checked = true;
        sizeInput.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    const finalVariant = variantData.find(v => 
      v.options[0] === colorOption && 
      v.options[1] === targetSize
    );

    if (finalVariant) {
      if (variantSelectEl.currentVariant !== undefined) {
        variantSelectEl.currentVariant = finalVariant;
      }
      
      if (typeof variantSelectEl.updateVariantInput === "function") {
        variantSelectEl.updateVariantInput();
      } else {
        const hiddenInput = document.querySelector('input[name="id"]');
        if (hiddenInput) {
          hiddenInput.value = finalVariant.id;
          hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      if (typeof variantSelectEl.updateURL === "function") {
        variantSelectEl.updateURL();
      }

      if (typeof variantSelectEl.renderProductInfo === "function") {
        variantSelectEl.renderProductInfo();
      }
      
      if (typeof variantSelectEl.updatePickupAvailability === "function") {
        variantSelectEl.updatePickupAvailability();
      }

      variantSelectEl.dispatchEvent(new CustomEvent("on:variant:change", {
        detail: { variant: finalVariant }
      }));

      document.dispatchEvent(new CustomEvent("variant:changed", {
        detail: { variant: finalVariant }
      }));
    }
  }

  function getVariantDataFromDOM() {
    const variantScript = document.querySelector('script[data-variant-data], script[type="application/json"][data-variants]');
    if (variantScript) {
      try {
        return JSON.parse(variantScript.textContent);
      } catch (e) {
        console.warn("Could not parse variant data from script tag");
      }
    }
    return [];
  }

  /**
   * Fetch variant gallery via AJAX
   */
  async function fetchVariantGallery(variantId, color, signal) {
    // Already cached by color?
    if (galleriesCache[color]) {
      return galleriesCache[color];
    }

    try {
      const fetchOptions = signal ? { signal } : {};

      // Tier 1: lean alternate template returns just JSON
      const resp = await fetch(`${window.location.pathname}?variant=${variantId}&view=variant-gallery`, fetchOptions);
      if (resp.ok) {
        const text = await resp.text();
        const trimmed = text.trim();

        // Try JSON first
        if (trimmed.startsWith('{')) {
          try {
            const data = JSON.parse(trimmed);
            if (data && Array.isArray(data.gallery)) {
              galleriesCache[color] = data.gallery;
              return data.gallery;
            }
          } catch (e) {}
        }

        // Fallback: response was full HTML (alternate template missing) — parse it
        try {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const galleryScript = doc.querySelector('#variant-galleries-data');
          if (galleryScript) {
            const fetchedGalleries = JSON.parse(galleryScript.textContent);
            const gallery = fetchedGalleries[variantId];
            if (gallery) {
              galleriesCache[color] = gallery;
              return gallery;
            }
          }
        } catch (e) {}
      }
    } catch (e) {
      if (e.name === 'AbortError') return null;
      console.error('Failed to fetch variant gallery:', e);
    }

    return null;
  }

  /**
   * Handle variant change (metafield mode)
   * Keeps current gallery visible until new images are preloaded.
   * Only reloads when COLOR changes — size changes are ignored.
   */
  async function onVariantChange(variantId) {
    if (!useMetafield) return;
    if (!variantId) return;
    window.scrollTo({ top: 0, behavior: 'smooth' }); 

    const newColor = variantColorMap[variantId];
    
    // If color hasn't changed, don't reload the gallery
    if (newColor === currentColor) {
      console.log("Size changed, keeping same gallery for color:", currentColor);
      currentVariantId = variantId;
      return;
    }
    
    console.log("Color changed from", currentColor, "to", newColor);
    currentVariantId = variantId;

    // Abort any previous in-flight switch
    if (switchAbortController) {
      switchAbortController.abort();
    }
    switchAbortController = new AbortController();
    const signal = switchAbortController.signal;

    const targetColor = newColor;

    // ---- CASE 1: Full gallery is already cached ----
    if (galleriesCache[targetColor]) {
      showLoadingOverlay();
      currentColor = targetColor;
      
      // Preload first image before swapping
      await preloadFirstImage(galleriesCache[targetColor]);
      if (signal.aborted) return;
      
      rebuildCarousel(galleriesCache[targetColor]);
      
      // Preload remaining images in background (non-blocking)
      preloadGallery(galleriesCache[targetColor]);
      return;
    }

    // ---- CASE 2: Need to fetch gallery ----
    // Show loading overlay ON TOP of current images (current carousel stays visible)
    showLoadingOverlay();

    // Get the variant ID for this color (from first images data)
    let fetchVariantId = variantId;
    if (colorFirstImages[targetColor] && colorFirstImages[targetColor].variantId) {
      fetchVariantId = colorFirstImages[targetColor].variantId;
    }

    // Fetch the gallery
    const gallery = await fetchVariantGallery(fetchVariantId, targetColor, signal);
    if (signal.aborted) return;
    
    // Only update if we're still targeting the same color
    if (gallery && gallery.length > 0) {
      currentColor = targetColor;
      
      // Preload at least the first image before swapping
      await preloadFirstImage(gallery);
      if (signal.aborted) return;
      
      rebuildCarousel(gallery);
      
      // Preload the rest in background
      preloadGallery(gallery);
    } else {
      // ---- CASE 3: Fetch failed, try first image from preloaded data ----
      const colorData = colorFirstImages[targetColor];
      if (colorData && colorData.firstImage) {
        const media = detectMediaType(colorData.firstImage);
        if (media && media.src) {
          // Preload the single image before swapping
          if (media.type === 'image') {
            const url = resizeImageUrl(fixShopifyUrl(media.src), IMAGE_SIZE);
            await new Promise((resolve) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = resolve;
              img.src = url;
              setTimeout(resolve, 3000);
            });
          }
          
          if (signal.aborted) return;
          
          currentColor = targetColor;
          rebuildCarousel([colorData.firstImage]);
          return;
        }
      }

      // ---- CASE 4: Nothing available ----
      if (!signal.aborted) {
        currentColor = targetColor;
        hideLoadingOverlay();
        track.innerHTML = '<div class="carousel-slide"><p style="color:#999;text-align:center;padding:20px;">No images available</p></div>';
        indicators.innerHTML = '';
      }
    }
  }

  // Handle slide change based on variant (fallback mode only)
  function onVariantSelected(variantId, options) {
    if (useMetafield) return;

    let target = null;
    if (variantId) {
      const key = String(variantId);
      if (variantIdToSlide.has(key)) {
        target = variantIdToSlide.get(key);
      }
    }

    if (target === null && options && options.length) {
      const optStr = options.join("#");
      if (optionKeyToSlide.has(optStr)) {
        target = optionKeyToSlide.get(optStr);
      }
    }

    if (typeof target === "number" && target !== currentIndex) {
      currentIndex = target;
      updateCarousel();
    }
  }

  // Navigation buttons
  prevBtn.addEventListener("click", () => {
    if (currentIndex > 0) { 
      currentIndex--; 
      updateCarousel(); 
    }
  });
  
  nextBtn.addEventListener("click", () => {
    const currentSlides = Array.from(document.querySelectorAll(".carousel-slide"));
    if (currentIndex < currentSlides.length - 1) { 
      currentIndex++; 
      updateCarousel(); 
    }
  });

  // Pointer gesture handling
  function onPointerDown(e) {
    if (e.target.tagName === 'IMG' || e.target.tagName === 'VIDEO') {
      return;
    }

    // Don't start a carousel drag when the gesture begins on a Zinzo hotspot.
    // Capturing the pointer here would retarget the pointerup/click to the
    // track and swallow the hotspot's own click (hover still fires, but the
    // tooltip never opens). Let the event reach the hotspot untouched.
    if (e.target.closest('.hotspot, #zinzo-native-hotspot-overlay')) {
      return;
    }
    
    isDragging = true;
    startX = e.clientX;
    startY = e.clientY;
    deltaX = 0;
    deltaY = 0;
    isHorizontalSwipe = null;
    startTime = Date.now();
    try { track.setPointerCapture(e.pointerId); } catch (err) {}
    track.style.transition = "none";
  }

  function onPointerMove(e) {
    if (!isDragging) return;

    deltaX = e.clientX - startX;
    deltaY = e.clientY - startY;

    if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return;

    if (isHorizontalSwipe === null) {
      if (Math.abs(deltaX) < MOVE_THRESHOLD && Math.abs(deltaY) < MOVE_THRESHOLD) return;

      isHorizontalSwipe = Math.abs(deltaX) > (Math.abs(deltaY) * DIRECTION_RATIO);

      if (!isHorizontalSwipe) {
        try { track.releasePointerCapture(e.pointerId); } catch (err) {}
        return;
      }
    }

    e.preventDefault();
    const currentSlides = Array.from(document.querySelectorAll(".carousel-slide"));
    if (currentSlides.length > 0) {
      const slideWidth = currentSlides[0].offsetWidth;
      track.style.transform = `translateX(${ -slideWidth * currentIndex + deltaX }px)`;
    }
  }

  function finishDrag(e) {
    if (!isDragging) return;
    
    const wasClick = Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5;
    
    isDragging = false;

    if (wasClick) {
      track.style.transition = "";
      updateCarousel();
      deltaX = 0;
      deltaY = 0;
      isHorizontalSwipe = null;
      try { track.releasePointerCapture(e?.pointerId); } catch (err) {}
      return;
    }

    if (isHorizontalSwipe !== true) {
      track.style.transition = "";
      updateCarousel();
      isHorizontalSwipe = null;
      return;
    }

    const elapsed = Math.max(1, Date.now() - startTime);
    const velocity = deltaX / elapsed;
    const currentSlides = Array.from(document.querySelectorAll(".carousel-slide"));
    const slideWidth = currentSlides.length > 0 ? currentSlides[0].offsetWidth : 300;
    const threshDistance = Math.max(MIN_SWIPE_DISTANCE, slideWidth * SWIPE_DISTANCE_RATIO);

    track.style.transition = "";

    if ((deltaX > threshDistance) || (velocity > SWIPE_VELOCITY)) {
      currentIndex = Math.max(0, currentIndex - 1);
    } else if ((deltaX < -threshDistance) || (velocity < -SWIPE_VELOCITY)) {
      currentIndex = Math.min(currentSlides.length - 1, currentIndex + 1);
    }

    updateCarousel();

    deltaX = 0;
    deltaY = 0;
    isHorizontalSwipe = null;
    try { track.releasePointerCapture(e?.pointerId); } catch (err) {}
  }

  track.addEventListener("pointerdown", onPointerDown);
  track.addEventListener("pointermove", onPointerMove, { passive: false });
  ["pointerup","pointercancel","pointerleave"].forEach(evt => track.addEventListener(evt, finishDrag));

  // Variant change listeners
  if (useMetafield) {
    document.addEventListener("variant:changed", (e) => {
      const variantId = e.detail?.variant?.id || e.detail?.selectedVariant?.id;
      if (variantId) {
        onVariantChange(variantId);
      }
    });

    const idInput = document.querySelector('input[name="id"]');
    if (idInput) {
      idInput.addEventListener("change", () => {
        if (idInput.value) {
          onVariantChange(idInput.value);
        }
      });

      try {
        let lastValue = idInput.value;
        const observer = new MutationObserver(() => {
          const currentValue = idInput.getAttribute("value") ?? idInput.value;
          if (currentValue !== lastValue) {
            lastValue = currentValue;
            if (currentValue) {
              onVariantChange(currentValue);
            }
          }
        });
        observer.observe(idInput, { attributes: true, attributeFilter: ["value"] });
      } catch (err) {
        console.error("MutationObserver error:", err);
      }
    }

    document.addEventListener("change", (e) => {
      if (e.target && e.target.name && (e.target.name.includes("options[") || e.target.name === "Color" || e.target.name === "Size")) {
        setTimeout(() => {
          const hiddenInput = document.querySelector('input[name="id"]');
          if (hiddenInput && hiddenInput.value) {
            onVariantChange(hiddenInput.value);
          }
        }, 100);
      }
    });
  } else {
    // Fallback mode variant listeners
    document.addEventListener("variant:changed", e => {
      const variantId = e.detail?.variant?.id || e.detail?.selectedVariant?.id;
      const options = Array.isArray(e.detail?.currentOptions) ? e.detail.currentOptions :
        Array.isArray(e.detail?.options) ? e.detail.options : null;
      onVariantSelected(variantId, options);
    });

    document.addEventListener("change", (e) => {
      try {
        if (e.target && e.target.name && e.target.name.indexOf("options[") === 0) {
          const opts = Array.from(document.querySelectorAll("[name^='options']")).map(s => s.value).filter(Boolean);
          if (opts.length) onVariantSelected(null, opts);
        }
      } catch (err) {}
    });

    const idInput = document.querySelector('input[name="id"]');
    if (idInput) {
      idInput.addEventListener("change", () => {
        onVariantSelected(idInput.value || null, null);
      });

      try {
        let last = idInput.value;
        const mo = new MutationObserver(muts => {
          const cur = idInput.getAttribute("value") ?? idInput.value;
          if (cur !== last) {
            last = cur;
            onVariantSelected(cur || null, null);
          }
        });
        mo.observe(idInput, { attributes: true, attributeFilter: ["value"] });
      } catch (err) {}
    }
  }

  // Initial setup
  const preRenderedSlides = track.querySelectorAll('.carousel-slide');
  const hasPreRenderedContent = preRenderedSlides.length > 0 && 
    (preRenderedSlides[0].querySelector('img') || preRenderedSlides[0].querySelector('video'));
  
  console.log("Desktop carousel init:", {
    useMetafield,
    preRenderedSlides: preRenderedSlides.length,
    hasPreRenderedContent,
    currentColor,
    hasCachedGallery: currentColor ? !!galleriesCache[currentColor] : false
  });
  
  if (useMetafield && hasPreRenderedContent) {
    console.log("Using pre-rendered slides for color:", currentColor);
    rebuildIndicators();
    updateCarousel();
  } else if (useMetafield && currentColor && galleriesCache[currentColor]) {
    console.log("Rebuilding from cache for color:", currentColor);
    rebuildCarousel(galleriesCache[currentColor]);
  } else if (useMetafield) {
    console.warn("Desktop carousel: No pre-rendered slides and no cache, attempting to fetch");
    if (currentVariantId && currentColor) {
      fetchVariantGallery(currentVariantId, currentColor).then(gallery => {
        if (gallery) {
          rebuildCarousel(gallery);
        }
      });
    }
  } else {
    rebuildIndicators();
    buildMaps();
    updateCarousel();
  }

  // Build thumbnails from server-rendered slides
  rebuildIndicators();

  window.addEventListener("resize", () => {
    setTimeout(updateCarousel, 50);
  });
});

// Carousel Zoom Class
class CarouselZoom {
  constructor(carouselSelector = '.custom-carousel-container') {
    this.carousel = document.querySelector(carouselSelector);
    this.defaultZoom = 2;
    this.minZoom = 1;
    this.maxZoom = 4;
    this.zoomStep = 0.25;
    this.currentZoom = 1;
    this.isPanning = false;
    this.startPan = { x: 0, y: 0 };
    this.pan = { x: 0, y: 0 };
    this.allMedia = [];
    this.currentMediaIndex = 0;
    this.init();
  }

  init() {
    if (!this.carousel) return;
    this.initClickZoom();
  }

  initClickZoom() {
    let overlay = document.querySelector('.zoom-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'zoom-overlay';
      overlay.innerHTML = `
        <button class="zoom-close-btn" aria-label="Close">×</button>
        <div class="zoom-inner">
          <button class="zoom-nav-btn zoom-prev-btn" aria-label="Previous">❮</button>
          <div class="zoom-pan-container">
            <img src="" alt="Zoomed image" draggable="false" style="display: none;"/>
            <video autoplay muted playsinline loop preload="auto" style="display: none; object-fit: contain;"></video>
          </div>
          <button class="zoom-nav-btn zoom-next-btn" aria-label="Next">❯</button>
        </div>
        <div class="zoom-thumbnails"></div>
      `;
      document.body.appendChild(overlay);
    }

    overlay.style.display = 'none';

    const zoomInner = overlay.querySelector('.zoom-inner');
    const panContainer = overlay.querySelector('.zoom-pan-container');
    const overlayImg = panContainer.querySelector('img');
    const overlayVideo = panContainer.querySelector('video');
    const closeBtn = overlay.querySelector('.zoom-close-btn');
    const thumbnailsContainer = overlay.querySelector('.zoom-thumbnails');
    const prevBtn = overlay.querySelector('.zoom-prev-btn');
    const nextBtn = overlay.querySelector('.zoom-next-btn');

    const closeOverlay = () => {
      overlay.classList.remove('active');
      overlay.style.display = 'none';
      this.resetZoom(panContainer);
    };

    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeOverlay();
    });

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target === zoomInner) {
        closeOverlay();
      }
    });

    prevBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateMedia(-1, overlay, overlayImg, overlayVideo, panContainer);
    });

    nextBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.navigateMedia(1, overlay, overlayImg, overlayVideo, panContainer);
    });

    const addClickListeners = () => {
      this.carousel.querySelectorAll('.image-container img, .image-container video').forEach(media => {
        media.removeEventListener('click', media._carouselZoomHandler);
        const handler = (e) => {
          this.openOverlay(e, overlay, overlayImg, overlayVideo, panContainer);
        };
        media._carouselZoomHandler = handler;
        media.addEventListener('click', handler);
      });
    };
    addClickListeners();

    const track = this.carousel.querySelector('.carousel-track');
    if (track) {
      const mo = new MutationObserver(addClickListeners);
      mo.observe(track, { childList: true, subtree: true });
    }

    const handleMediaClick = (e) => {
      if (this.wasPanning) {
        this.wasPanning = false;
        return;
      }
      
      e.stopPropagation();
      if (this.currentZoom > 1) {
        this.resetZoom(panContainer);
      } else {
        const rect = panContainer.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        this.zoomToPoint(this.defaultZoom, cx, cy, panContainer);
      }
    };
    
    overlayImg.addEventListener('click', handleMediaClick);
    overlayVideo.addEventListener('click', handleMediaClick);
    panContainer.addEventListener('click', handleMediaClick);

    document.addEventListener('keydown', (e) => {
      if (overlay.style.display === 'none') return;
      
      if (e.key === 'Escape') {
        closeOverlay();
      } else if (e.key === 'ArrowLeft') {
        this.navigateMedia(-1, overlay, overlayImg, overlayVideo, panContainer);
      } else if (e.key === 'ArrowRight') {
        this.navigateMedia(1, overlay, overlayImg, overlayVideo, panContainer);
      }
    });

    overlayImg.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (this.currentZoom > 1) {
        this.resetZoom(panContainer);
      } else {
        const rect = panContainer.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        this.zoomToPoint(this.defaultZoom, cx, cy, panContainer);
      }
    });

    overlayVideo.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (this.currentZoom > 1) {
        this.resetZoom(panContainer);
      } else {
        const rect = panContainer.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;
        this.zoomToPoint(this.defaultZoom, cx, cy, panContainer);
      }
    });

    overlay.addEventListener('wheel', (e) => {
      if (!overlay.classList.contains('active')) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -this.zoomStep : this.zoomStep;
      const rect = panContainer.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const newZoom = Math.min(this.maxZoom, Math.max(this.minZoom, this.currentZoom + delta));
      this.zoomRelative(newZoom, { x: cx, y: cy }, panContainer);
    }, { passive: false });

    panContainer.addEventListener('pointerdown', (e) => {
      if (this.currentZoom <= 1) return;
      this.isPanning = true;
      this.hasMoved = false;
      panContainer.setPointerCapture(e.pointerId);
      panContainer.style.cursor = 'grabbing';
      this.startPan = { x: e.clientX - this.pan.x, y: e.clientY - this.pan.y };
      e.preventDefault();
    });

    panContainer.addEventListener('pointermove', (e) => {
      if (!this.isPanning) return;
      this.hasMoved = true;
      this.pan.x = e.clientX - this.startPan.x;
      this.pan.y = e.clientY - this.startPan.y;
      this.applyTransform(panContainer);
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach(evt => {
      panContainer.addEventListener(evt, (e) => {
        if (!this.isPanning) return;
        if (this.hasMoved) {
          this.wasPanning = true;
          setTimeout(() => { this.wasPanning = false; }, 100);
        }
        this.isPanning = false;
        this.hasMoved = false;
        try { panContainer.releasePointerCapture(e.pointerId); } catch (err) {}
        panContainer.style.cursor = '';
      });
    });
  }

  openOverlay(e, overlay, overlayImg, overlayVideo, panContainer) {
    e.preventDefault();
    e.stopPropagation();

    const media = e.currentTarget;
    this.allMedia = Array.from(this.carousel.querySelectorAll('.carousel-slide img, .carousel-slide video'));
    this.currentMediaIndex = this.allMedia.indexOf(media);
    
    this.displayMedia(this.currentMediaIndex, overlay, overlayImg, overlayVideo, panContainer);
    this.buildThumbnails(overlay, overlayImg, overlayVideo, panContainer);

    overlay.style.display = 'flex';
    
    requestAnimationFrame(() => {
      overlay.classList.add('active');
    });

    this.pan = { x: 0, y: 0 };
    this.currentZoom = 1;
    this.applyTransform(panContainer);
    this.updateCursor(panContainer);
  }

  displayMedia(index, overlay, overlayImg, overlayVideo, panContainer) {
    if (index < 0 || index >= this.allMedia.length) return;
    
    const mediaEl = this.allMedia[index];
    const isVideo = mediaEl.tagName === 'VIDEO';
    
    if (isVideo) {
      overlayVideo.src = mediaEl.src;
      overlayVideo.poster = mediaEl.poster || '';
      overlayVideo.style.display = 'block';
      overlayImg.style.display = 'none';
    } else {
      overlayImg.src = mediaEl.src;
      overlayImg.style.display = 'block';
      overlayVideo.style.display = 'none';
    }
    
    const prevBtn = overlay.querySelector('.zoom-prev-btn');
    const nextBtn = overlay.querySelector('.zoom-next-btn');
    
    if (prevBtn) prevBtn.style.display = index > 0 ? 'flex' : 'none';
    if (nextBtn) nextBtn.style.display = index < this.allMedia.length - 1 ? 'flex' : 'none';
    
    this.resetZoom(panContainer);
  }

  navigateMedia(direction, overlay, overlayImg, overlayVideo, panContainer) {
    const newIndex = this.currentMediaIndex + direction;
    
    if (newIndex >= 0 && newIndex < this.allMedia.length) {
      this.currentMediaIndex = newIndex;
      this.displayMedia(this.currentMediaIndex, overlay, overlayImg, overlayVideo, panContainer);
      this.updateActiveThumbnail(overlay);
    }
  }

  buildThumbnails(overlay, overlayImg, overlayVideo, panContainer) {
    const thumbnailsContainer = overlay.querySelector('.zoom-thumbnails');
    if (!thumbnailsContainer) return;

    thumbnailsContainer.innerHTML = '';
    
    this.allMedia.forEach((mediaEl, index) => {
      const thumb = document.createElement('div');
      thumb.className = 'zoom-thumb';
      
      if (index === this.currentMediaIndex) {
        thumb.classList.add('active');
      }

      const isVideo = mediaEl.tagName === 'VIDEO';
      
      if (isVideo) {
        const videoThumb = document.createElement('video');
        videoThumb.src = mediaEl.src;
        videoThumb.poster = mediaEl.poster || '';
        videoThumb.muted = true;
        thumb.appendChild(videoThumb);
      } else {
        const imgThumb = document.createElement('img');
        imgThumb.src = mediaEl.src;
        imgThumb.alt = `Thumbnail ${index + 1}`;
        thumb.appendChild(imgThumb);
      }

      thumb.addEventListener('click', (e) => {
        e.stopPropagation();
        this.currentMediaIndex = index;
        this.displayMedia(index, overlay, overlayImg, overlayVideo, panContainer);
        this.updateActiveThumbnail(overlay);
      });

      thumbnailsContainer.appendChild(thumb);
    });
  }

  updateActiveThumbnail(overlay) {
    const thumbnailsContainer = overlay.querySelector('.zoom-thumbnails');
    if (!thumbnailsContainer) return;
    
    thumbnailsContainer.querySelectorAll('.zoom-thumb').forEach((thumb, index) => {
      thumb.classList.toggle('active', index === this.currentMediaIndex);
    });
    
    const activeThumb = thumbnailsContainer.querySelector('.zoom-thumb.active');
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  zoomToPoint(zoom, cx, cy, panContainer) {
    zoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
    const prevZoom = this.currentZoom;
    const rect = panContainer.getBoundingClientRect();

    const rx = cx / rect.width;
    const ry = cy / rect.height;

    const deltaScale = zoom / prevZoom;
    this.pan.x = this.pan.x - (rect.width * (deltaScale - 1) * (rx - 0.5));
    this.pan.y = this.pan.y - (rect.height * (deltaScale - 1) * (ry - 0.5));

    this.currentZoom = zoom;
    this.applyTransform(panContainer);
    this.updateCursor(panContainer);
  }

  zoomRelative(newZoom, focusPoint, panContainer) {
    newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));
    const prev = this.currentZoom;
    if (newZoom === prev) return;

    const scaleDelta = newZoom / prev;
    this.pan.x = this.pan.x - (focusPoint.x) * (scaleDelta - 1);
    this.pan.y = this.pan.y - (focusPoint.y) * (scaleDelta - 1);

    this.currentZoom = newZoom;
    this.applyTransform(panContainer);
    this.updateCursor(panContainer);
  }

  resetZoom(panContainer) {
    this.currentZoom = 1;
    this.pan = { x: 0, y: 0 };
    this.applyTransform(panContainer);
    this.updateCursor(panContainer);
  }

  updateCursor(panContainer) {
    if (this.currentZoom > 1) {
      panContainer.style.cursor = 'zoom-out';
    } else {
      panContainer.style.cursor = 'zoom-in';
    }
  }

  applyTransform(panContainer) {
    const img = panContainer.querySelector('img');
    const video = panContainer.querySelector('video');
    const rect = panContainer.getBoundingClientRect();
    const maxX = Math.max(0, (this.currentZoom - 1) * rect.width);
    const maxY = Math.max(0, (this.currentZoom - 1) * rect.height);
    this.pan.x = Math.max(-maxX, Math.min(maxX, this.pan.x));
    this.pan.y = Math.max(-maxY, Math.min(maxY, this.pan.y));
    
    const transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.currentZoom})`;
    if (img) img.style.transform = transform;
    if (video) video.style.transform = transform;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    new CarouselZoom('.custom-carousel-container');
  }, 100);
});